import i18n from "../i18n";

function toCamelCase(str) {
  return str.replace(/([-_][a-z])/gi, (s) =>
    s.toUpperCase().replace("-", "").replace("_", ""),
  );
}

function keysToCamel(o) {
  if (Array.isArray(o)) {
    return o.map((v) => keysToCamel(v));
  } else if (o !== null && typeof o === "object" && o.constructor === Object) {
    return Object.keys(o).reduce((acc, key) => {
      acc[toCamelCase(key)] = keysToCamel(o[key]);
      return acc;
    }, {});
  }
  return o;
}

// Єдина база API. Змінюється через REACT_APP_API_BASE у .env
const BASE_URL =
  process.env.REACT_APP_API_BASE || "http://localhost:5001/apiV1";

const request = async (method, url, data = null) => {
  const config = {
    method,
    headers: {
      "Content-Type": "application/json",
      // Мова інтерфейсу -> сервер віддає повідомлення/помилки цією мовою
      "Accept-Language": i18n.language || "pl",
    },
    credentials: "include",
  };

  if (data !== null && data !== undefined) {
    config.body = JSON.stringify(data);
  }

  const response = await fetch(`${BASE_URL}${url}`, config);

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || i18n.t("common.networkError"));
    err.status = response.status; // типізована помилка: перевіряй e.status
    if (body.fields) err.fields = body.fields; // помилки по полях для inline-підсвітки
    throw err;
  }

  const raw = await response.json().catch(() => null);
  return keysToCamel(raw);
};

// Заглушка для функцій, яких ще немає на бекенді (OTP, Stripe).
// Показує зрозумілий алерт замість падіння в 404.
const notImplemented = (feature) => {
  const msg = i18n.t("stub.inProgress", { feature });
  if (typeof window !== "undefined") window.alert(msg);
  return Promise.reject(new Error(msg));
};

export const api = {
  // --- Auth ---
  login: (identifier, password) =>
    request("POST", "/auth/login", { identifier, password }),
  logout: () => request("POST", "/auth/logout"),
  getCurrentUser: () => request("GET", "/users/me"),
  register: (payload) => request("POST", "/auth/register", payload),
  requestOtp: (phone, purpose) => request("POST", "/auth/request-otp", { phone, purpose }),
  verifyOtp: (payload) => request("POST", "/auth/verify-otp", payload),

  // --- Bookings ---
  getBookings: () => request("GET", "/bookings"),
  getBookingById: (id) => request("GET", `/bookings/${id}`),
  createBooking: (data) => request("POST", "/bookings", data),
  cancelBooking: (id) => request("DELETE", `/bookings/${id}`),
  updateBooking: (id, data) => request("PATCH", `/bookings/${id}`, data),
  checkoutBooking: (id, payload) =>
    request("POST", `/bookings/${id}/checkout`, payload),
  notifyClient: (id, payload) => request("POST", `/bookings/${id}/notify`, payload),
  createBookingLink: (params) =>
    request("POST", "/bookings/booking-links", params),
  getBookingLink: (hash) => request("GET", `/bookings/booking-links/${hash}`),
  getBookingConfirm: (hash) => request("GET", `/bookings/confirm/${hash}`),
  submitBookingConfirm: (hash, payload) =>
    request("POST", `/bookings/confirm/${hash}`, payload),
  getAvailability: (masterId, date, services) =>
    request(
      "GET",
      `/bookings/availability?masterId=${masterId}&date=${date}&services=${(services || []).join(",")}`,
    ),

  // --- Promo codes ---
  getPromos: () => request("GET", "/promos"),
  createPromo: (data) => request("POST", "/promos", data),
  updatePromo: (id, data) => request("PATCH", `/promos/${id}`, data),
  deletePromo: (id) => request("DELETE", `/promos/${id}`),
  validatePromo: (masterId, code, amount) =>
    request("GET", `/promos/validate?masterId=${masterId}&code=${encodeURIComponent(code)}&amount=${amount || 0}`),

  // --- Services ---
  getBookingTerms: (masterId) => request("GET", `/users/${masterId}/terms`),
  updateBookingTerms: (masterId, text) => request("PATCH", `/users/${masterId}/terms`, { text }),
  getMyClients: () => request("GET", "/bookings/clients"),
  getClientBookings: (userId) => request("GET", `/bookings/client/${userId}`),
  getServices: (masterId) =>
    request("GET", masterId ? `/services?masterId=${masterId}` : "/services"),
  createService: (data) => request("POST", "/services", data),
  updateService: (id, data) => request("PATCH", `/services/${id}`, data),
  deleteService: (id) => request("DELETE", `/services/${id}`),

  // --- Users ---
  getMasters: () => request("GET", "/users/masters"),
  getUsers: () => request("GET", "/users"),
  getUsersByOrg: () => request("GET", "/users"),
  createUser: (data) => request("POST", "/users", data),
  updateUser: (id, data) => request("PATCH", `/users/${id}`, data),
  // Завантаження аватара (multipart) — окремо від JSON-хелпера
  uploadAvatar: async (file) => {
    const fd = new FormData();
    fd.append("avatar", file);
    const res = await fetch(`${BASE_URL}/users/me/avatar`, {
      method: "POST",
      body: fd, // Content-Type виставляє браузер (з boundary)
      credentials: "include",
      headers: { "Accept-Language": i18n.language || "pl" },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || i18n.t("common.networkError"));
      err.status = res.status;
      throw err;
    }
    return res.json();
  },
  updateUserStatus: (id, status) =>
    request("PATCH", `/users/${id}/status`, { status }),
  getUserByPhone: (phone) => request("GET", `/users/phone/${phone}`),
  getUserByEmail: (email) => request("GET", `/users/email/${email}`),
  getUserById: (id) => request("GET", `/users/id/${id}`),

  // --- Schedule (розклад майстра) ---
  getSchedule: () => request("GET", "/schedule"),
  updateWorkHours: (workHours) =>
    request("PUT", "/schedule/work-hours", { workHours }),
  addTimeOff: (payload) => request("POST", "/schedule/time-off", payload),
  updateTimeOff: (id, payload) => request("PATCH", `/schedule/time-off/${id}`, payload),
  deleteTimeOff: (id) => request("DELETE", `/schedule/time-off/${id}`),
  setDayOverride: (payload) => request("POST", "/schedule/day-override", payload),
  deleteDayOverride: (id) => request("DELETE", `/schedule/day-override/${id}`),
  getPublicSchedule: (masterId) =>
    request("GET", `/schedule/public?masterId=${masterId}`),

  // --- Checkouts ---
  getCheckouts: () => request("GET", "/checkouts"),
  getCheckoutsByBookingId: (id) =>
    request("GET", `/checkouts/checkout-by-booking-id/${id}`),

  // --- Payments (є на бекенді) ---
  createPaymentLink: (params) =>
    request("POST", "/payments/payment-links", params),
  getPaymentLink: (hash) => request("GET", `/payments/payment-links/${hash}`),
  confirmPayment: (hash, payload) =>
    request("POST", `/payments/payment-links/${hash}/confirm`, payload),

  // --- Заглушки (ендпоінтів ще немає на бекенді) ---
  createStripeAccount: () => notImplemented(i18n.t("stub.stripeConnect")),
  removeStripeAccount: () => notImplemented(i18n.t("stub.stripeDisconnect")),
  getStripeSetupLink: () => notImplemented(i18n.t("stub.stripeSetup")),
};
